'use client';
import './features.scss';
import { LANDING_OR_PRODUCT_PAGE_QUERY_RESULT } from '@/sanity.types';
import QuoteBlock from './QuoteBlock';
import FeatureContentBlock from './FeatureContentBlock';

interface FeatureSectionProps {
  data: NonNullable<LANDING_OR_PRODUCT_PAGE_QUERY_RESULT>;
}

export default function FeaturesMobile({ data }: FeatureSectionProps) {
  const featureContent =
    data?._type === 'productPage' ? data?.featuresTiles : null;

  // Only the first rendered card keeps its image; every card after that
  // hides its image on mobile. Cards without an image are still counted
  // so later cards line up correctly.
  let cardIndex = 0;
  const nextHideImage = () => cardIndex++ > 0;

  return (
    <div className="features-mobile-stack">
      {featureContent?.featuresBricks
        ?.map((brick, brickIndex) => {
          const cards = [];

          if (brick?._type === 'megaBrick') {
            // BigBlock content card
            if (brick?.bigBlock?.blockType === 'content') {
              cards.push(
                <div
                  key={`mega-big-${brickIndex}`}
                  className="features-list-left"
                >
                  <FeatureContentBlock
                    contentBlock={brick?.bigBlock?.contentBlock}
                    hideImage={nextHideImage()}
                  />
                </div>
              );
            } else if (brick?.bigBlock?.blockType === 'quote') {
              nextHideImage();
              cards.push(
                <div key={`mega-big-quote-${brickIndex}`}>
                  <QuoteBlock
                    quote={brick?.bigBlock?.quoteBlock?.quote}
                    author={brick?.bigBlock?.quoteBlock?.author}
                    backgroundColor={featureContent?.featuredColor?.value}
                  />
                </div>
              );
            }

            // SmallBlockTop content card
            if (brick?.smallBlockTop?.blockType === 'content') {
              cards.push(
                <div key={`mega-small-top-${brickIndex}`}>
                  <FeatureContentBlock
                    contentBlock={brick?.smallBlockTop?.contentBlock}
                    hideImage={nextHideImage()}
                  />
                </div>
              );
            } else if (brick?.smallBlockTop?.blockType === 'quote') {
              nextHideImage();
              cards.push(
                <div key={`mega-small-top-quote-${brickIndex}`}>
                  <QuoteBlock
                    quote={brick?.smallBlockTop?.quoteBlock?.quote}
                    author={brick?.smallBlockTop?.quoteBlock?.author}
                    backgroundColor={featureContent?.featuredColor?.value}
                  />
                </div>
              );
            }

            // SmallBlockBottom content card
            if (brick?.smallBlockBottom?.blockType === 'content') {
              cards.push(
                <div key={`mega-small-bottom-${brickIndex}`}>
                  <FeatureContentBlock
                    contentBlock={brick?.smallBlockBottom?.contentBlock}
                    hideImage={nextHideImage()}
                  />
                </div>
              );
            } else if (brick?.smallBlockBottom?.blockType === 'quote') {
              nextHideImage();
              cards.push(
                <div key={`mega-small-bottom-quote-${brickIndex}`}>
                  <QuoteBlock
                    quote={brick?.smallBlockBottom?.quoteBlock?.quote}
                    author={brick?.smallBlockBottom?.quoteBlock?.author}
                    backgroundColor={featureContent?.featuredColor?.value}
                  />
                </div>
              );
            }
          }

          if (brick?._type === 'tripleColumn') {
            const leftHideImage = nextHideImage();
            const centerHideImage = nextHideImage();
            const rightHideImage = nextHideImage();

            cards.push(
              <div
                key={`triple-columns-${brickIndex}`}
                className="features-list triple-column-block"
              >
                {brick?.leftColumn?.blockType === 'content' && (
                  <FeatureContentBlock
                    contentBlock={brick?.leftColumn?.contentBlock}
                    hideImage={leftHideImage}
                  />
                )}
                {brick?.leftColumn?.blockType === 'quote' && (
                  <QuoteBlock
                    quote={brick?.leftColumn?.quoteBlock?.quote}
                    author={brick?.leftColumn?.quoteBlock?.author}
                    backgroundColor={featureContent?.featuredColor?.value}
                  />
                )}
                {brick?.centerColumn?.blockType === 'content' && (
                  <FeatureContentBlock
                    contentBlock={brick?.centerColumn?.contentBlock}
                    hideImage={centerHideImage}
                  />
                )}
                {brick?.centerColumn?.blockType === 'quote' && (
                  <QuoteBlock
                    quote={brick?.centerColumn?.quoteBlock?.quote}
                    author={brick?.centerColumn?.quoteBlock?.author}
                    backgroundColor={featureContent?.featuredColor?.value}
                  />
                )}
                {brick?.rightColumn?.blockType === 'content' && (
                  <FeatureContentBlock
                    contentBlock={brick?.rightColumn?.contentBlock}
                    hideImage={rightHideImage}
                  />
                )}
                {brick?.rightColumn?.blockType === 'quote' && (
                  <QuoteBlock
                    quote={brick?.rightColumn?.quoteBlock?.quote}
                    author={brick?.rightColumn?.quoteBlock?.author}
                    backgroundColor={featureContent?.featuredColor?.value}
                  />
                )}
              </div>
            );
          }

          if (brick?._type === 'doubleColumn') {
            // Left column card
            if (brick?.leftColumn?.blockType === 'content') {
              cards.push(
                <div key={`double-left-content-${brickIndex}`}>
                  <FeatureContentBlock
                    contentBlock={brick?.leftColumn?.contentBlock}
                    hideImage={nextHideImage()}
                  />
                </div>
              );
            } else if (brick?.leftColumn?.blockType === 'quote') {
              nextHideImage();
              cards.push(
                <div key={`double-left-quote-${brickIndex}`}>
                  <QuoteBlock
                    quote={brick?.leftColumn?.quoteBlock?.quote}
                    author={brick?.leftColumn?.quoteBlock?.author}
                    backgroundColor={featureContent?.featuredColor?.value}
                  />
                </div>
              );
            }

            // Right column card
            if (brick?.rightColumn?.blockType === 'content') {
              cards.push(
                <div key={`double-right-content-${brickIndex}`}>
                  <FeatureContentBlock
                    contentBlock={brick?.rightColumn?.contentBlock}
                    hideImage={nextHideImage()}
                  />
                </div>
              );
            } else if (brick?.rightColumn?.blockType === 'quote') {
              nextHideImage();
              cards.push(
                <div key={`double-right-quote-${brickIndex}`}>
                  <QuoteBlock
                    quote={brick?.rightColumn?.quoteBlock?.quote}
                    author={brick?.rightColumn?.quoteBlock?.author}
                    backgroundColor={featureContent?.featuredColor?.value}
                  />
                </div>
              );
            }
          }

          if (brick?._type === 'singleColumn') {
            if (brick?.blockType === 'content') {
              cards.push(
                <div key={`single-content-${brickIndex}`}>
                  <FeatureContentBlock
                    contentBlock={brick?.contentBlock}
                    hideImage={nextHideImage()}
                    className={`single-column-feature ${brick?.contentBlock?.image ? 'has-image-list' : ''} ${brick?.contentBlock?.toggleColumnBackgroundColor ? 'background' : 'no-background'}`}
                  />
                </div>
              );
            } else if (brick?.blockType === 'quote') {
              nextHideImage();
              cards.push(
                <div key={`single-quote-${brickIndex}`}>
                  <QuoteBlock
                    quote={brick?.quoteBlock?.quote || ''}
                    author={brick?.quoteBlock?.author || ''}
                    backgroundColor={featureContent?.featuredColor?.value}
                  />
                </div>
              );
            }
          }

          return cards;
        })
        .flat()}
    </div>
  );
}
